/**
 * Hook: all dashboard queries, favorites, ecosystem stats, sparks payment
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchUserFavorites, toggleFavorite } from '@/services/favorites';
import { logRequest } from '@/hooks/useRequestLogger';
import { useToolSettings } from '@/hooks/useToolSettings';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useMarketplaceGlobalMetrics } from '@/hooks/useMarketplaceGlobalMetrics';
import { toast } from 'sonner';
import { useEagerPrefetch } from '@/hooks/useEagerPrefetch';
import { useComponentPrefetch } from '@/hooks/useComponentPrefetch';
import { useSparksRoleSync } from '@/hooks/useSparksRoleSync';
import { tools } from '@/data/tools';
import { getEffectiveToolStatus } from '@/services/toolSettings';

// ─── Favorites cache ───
const FAVORITES_CACHE_PREFIX = 'forja:favorites:v1';
const getFavoritesCacheKey = (userId: string) => `${FAVORITES_CACHE_PREFIX}:${userId}`;

const readFavoritesCache = (userId: string): string[] | null => {
  try {
    const raw = localStorage.getItem(getFavoritesCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ids?: unknown };
    const ids = parsed?.ids;
    if (!Array.isArray(ids)) return null;
    return ids.filter((v): v is string => typeof v === 'string');
  } catch { return null; }
};

const writeFavoritesCache = (userId: string, ids: string[]) => {
  try {
    localStorage.setItem(getFavoritesCacheKey(userId), JSON.stringify({ ids, updatedAt: Date.now() }));
  } catch { /* ignore */ }
};

const getInitialFavorites = (userId: string | undefined): string[] => {
  if (!userId) return [];
  return readFavoritesCache(userId) ?? [];
};

// ─── Ecosystem stats ───
async function fetchEcosystemStats() {
  const [creatorsRes, productsRes, videosRes] = await Promise.all([
    supabase.from('arsenal_creators').select('id', { count: 'exact', head: true })
      .gt('follower_count', 0).gt('heart_count', 0).gt('video_count', 0),
    supabase.from('trending_products_cache').select('id', { count: 'exact', head: true }),
    supabase.from('arsenal_videos').select('id', { count: 'exact', head: true })
      .eq('intention', 'tiktok_shop').eq('publication_type', 'normal'),
  ]);
  logRequest('arsenal_creators');
  logRequest('marketplace_products');
  logRequest('arsenal_videos');
  return {
    creatorsCount: creatorsRes.count ?? 0,
    productsCount: productsRes.count ?? 0,
    viralVideosCount: videosRes.count ?? 0,
  };
}

const EXCLUDED_TOOL_IDS = ['minha-forja', 'marketplace', 'parceiros-forja'];

export function useDashboardData() {
  const { user } = useAuth();
  const { effectiveRole, loadingRole } = useRole();
  const { data: currentProfile } = useCurrentProfile(user?.id);
  const userName = currentProfile?.display_name || user?.email || 'Usuário';
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { getSettingByToolId, loading: settingsLoading } = useToolSettings();
  const { metrics, isLoading: metricsLoading } = useDashboardMetrics();
  const { metrics: marketplaceMetrics } = useMarketplaceGlobalMetrics();

  // ─── Prefetch hooks ───
  useEagerPrefetch();
  useComponentPrefetch();
  useSparksRoleSync();

  // ─── Queries ───
  const { data: ecosystemStats } = useQuery({
    queryKey: ['ecosystem-stats'],
    queryFn: fetchEcosystemStats,
    staleTime: 5 * 60 * 1000,
  });

  // ─── Marketplace tool status ───
  const marketplaceTool = tools.find(t => t.id === 'marketplace');
  const marketplaceSetting = getSettingByToolId('marketplace');
  const marketplaceStatus = marketplaceTool
    ? getEffectiveToolStatus('marketplace', effectiveRole, marketplaceSetting, marketplaceTool.status)
    : { status: 'active' as const };

  // ─── Sparks payment flow ───
  useEffect(() => {
    const sparksPayment = searchParams.get('sparks_payment');
    if (!sparksPayment) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('sparks_payment');
    setSearchParams(newParams, { replace: true });

    if (sparksPayment === 'success' && user?.id) {
      import('@/services/sparks').then(({ verifySparksPayment }) => {
        verifySparksPayment()
          .then((result) => {
            queryClient.invalidateQueries({ queryKey: ['sparks-wallet', user.id] });
            toast('⚡ Compra confirmada!', { description: result.credited
                ? `${result.total_credited} Faíscas foram creditadas com sucesso.`
                : 'Suas Faíscas serão creditadas em instantes.' });
          })
          .catch(() => {
            queryClient.invalidateQueries({ queryKey: ['sparks-wallet', user.id] });
            toast('⚡ Pagamento recebido!', { description: 'Suas Faíscas serão creditadas em instantes.' });
          });
      });
    } else if (sparksPayment === 'cancelled') {
      toast.error('Compra cancelada', { description: 'Você pode tentar novamente a qualquer momento.' });
    }
  }, [searchParams, setSearchParams, user?.id, queryClient, toast]);

  // ─── Favorites ───
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => getInitialFavorites(user?.id));
  const hasAnimated = useRef(user?.id && readFavoritesCache(user.id) ? true : false);

  useEffect(() => {
    if (!user?.id) { setFavoriteIds([]); return; }
    const cached = readFavoritesCache(user.id);
    if (cached) hasAnimated.current = true;
    const syncWithBackend = async () => {
      try {
        const favorites = await fetchUserFavorites(user.id);
        writeFavoritesCache(user.id, favorites);
        setFavoriteIds(favorites);
      } catch { /* ignore */ }
      finally { hasAnimated.current = true; }
    };
    syncWithBackend();
  }, [user?.id]);

  const handleToggleFavorite = useCallback(async (toolId: string) => {
    if (!user?.id) {
      toast.error("Faça login", { description: "Você precisa estar logado para favoritar ferramentas." });
      return;
    }
    const isFavorite = favoriteIds.includes(toolId);
    const updateFavorites = (updater: (prev: string[]) => string[]) => {
      setFavoriteIds(prev => { const next = updater(prev); writeFavoritesCache(user.id, next); return next; });
    };
    if (isFavorite) { updateFavorites(prev => prev.filter(id => id !== toolId)); }
    else { updateFavorites(prev => [...prev, toolId]); }
    const success = await toggleFavorite(user.id, toolId, isFavorite);
    if (!success) {
      if (isFavorite) { updateFavorites(prev => [...prev, toolId]); }
      else { updateFavorites(prev => prev.filter(id => id !== toolId)); }
      toast.error("Erro", { description: "Não foi possível atualizar seus favoritos." });
    }
  }, [user?.id, favoriteIds, toast]);

  // ─── Computed ───
  const explorarTools = useMemo(() => tools.filter(tool => !tool.isFixed && !EXCLUDED_TOOL_IDS.includes(tool.id)), []);
  const skipAnimation = hasAnimated.current;

  // Progressive rendering — removed isFullyReady gate
  const isFullyReady = true;

  return {
    user, userName, effectiveRole,
    metrics, marketplaceMetrics, ecosystemStats,
    marketplaceStatus,
    favoriteIds, handleToggleFavorite, skipAnimation,
    explorarTools, getSettingByToolId,
    isFullyReady,
  };
}
